import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  Upload, FileText, Download, Eye, Trash2, 
  File, Image, Archive, Video, Music, X, Plus
} from 'lucide-react';
import { format } from 'date-fns';

const DocumentManager = ({ project, onDocumentUpdate, userRole }) => {
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [viewingDocument, setViewingDocument] = useState(null);

  const getFileIcon = (fileType) => {
    if (fileType?.startsWith('image/')) return <Image className="w-8 h-8" />;
    if (fileType?.startsWith('video/')) return <Video className="w-8 h-8" />;
    if (fileType?.startsWith('audio/')) return <Music className="w-8 h-8" />;
    if (fileType?.includes('pdf')) return <FileText className="w-8 h-8" />;
    if (fileType?.includes('zip') || fileType?.includes('rar')) return <Archive className="w-8 h-8" />;
    return <File className="w-8 h-8" />;
  };

  const getFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleFileSelect = (file) => {
    // Check file size (50MB limit)
    if (file.size > 50 * 1024 * 1024) {
      toast.error('File size must be less than 50MB');
      return;
    }

    // Check file type
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain',
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'application/zip',
      'application/x-rar-compressed'
    ];

    if (!allowedTypes.includes(file.type)) {
      toast.error('Unsupported file type');
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('document', selectedFile);

    try {
      await axios.post(`/api/upload/document/${project._id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      toast.success('Document uploaded successfully!');
      setSelectedFile(null);
      setShowUpload(false);
      onDocumentUpdate();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      await axios.delete(`/api/upload/document/${project._id}/${documentId}`);
      toast.success('Document deleted successfully');
      onDocumentUpdate();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleDownload = async (doc) => {
    try {
      const token = localStorage.getItem('token');
      const downloadUrl = `/api/upload/document-proxy/${project._id}/${doc._id}?download=true&token=${encodeURIComponent(token)}`;
      
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = doc.name;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download document');
    }
  };

  const handleView = async (doc) => {
    try {
      const token = localStorage.getItem('token');
      const viewUrl = `/api/upload/document-proxy/${project._id}/${doc._id}?token=${encodeURIComponent(token)}`;
      
      // Open in new window for viewing
      window.open(viewUrl, '_blank');
    } catch (error) {
      console.error('View error:', error);
      toast.error('Failed to view document');
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileSelect(files[0]);
    }
  };

  const viewDocument = (document) => {
    setViewingDocument(document);
  };

  const getViewerContent = (doc) => {
    const { url, fileType, name, _id } = doc;
    
    // Get token from localStorage for authentication
    const token = localStorage.getItem('token');
    
    // Use proxy URL for document access to handle authentication
    const proxyUrl = `/api/upload/document-proxy/${project._id}/${_id}${token ? `?token=${token}` : ''}`;
    const documentUrl = url.includes('cloudinary.com') ? proxyUrl : url;
    
    // For images, show directly
    if (fileType?.startsWith('image/')) {
      return (
        <img 
          src={documentUrl} 
          alt={name}
          className="max-w-full max-h-full object-contain"
        />
      );
    }
    
    // For PDFs, provide multiple viewing options
    if (fileType?.includes('pdf')) {
      return (
        <div className="flex flex-col items-center justify-center h-full space-y-6">
          <div className="text-center">
            <div className="text-primary-600 mb-4">
              <FileText className="w-16 h-16 mx-auto" />
            </div>
            <p className="text-lg font-medium text-gray-800 mb-2">{name}</p>
            <p className="text-gray-600 mb-6">
              PDF Document - Choose how to view this file
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full max-w-2xl">
            <button
              onClick={() => handleView(doc)}
              className="bg-primary-600 text-white px-6 py-4 rounded-lg hover:bg-primary-700 transition-colors text-center flex flex-col items-center space-y-2"
            >
              <Eye className="w-6 h-6" />
              <span className="font-medium">Open in New Tab</span>
              <span className="text-xs opacity-90">Best for viewing</span>
            </button>
            
            <button
              onClick={() => {
                // For Google Docs viewer, we need a publicly accessible URL
                const googleViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(doc.url)}`;
                window.open(googleViewerUrl, '_blank');
              }}
              className="bg-blue-600 text-white px-6 py-4 rounded-lg hover:bg-blue-700 transition-colors text-center flex flex-col items-center space-y-2"
            >
              <FileText className="w-6 h-6" />
              <span className="font-medium">Google Viewer</span>
              <span className="text-xs opacity-90">Alternative viewer</span>
            </button>
            
            <button
              onClick={() => handleDownload(doc)}
              className="bg-gray-600 text-white px-6 py-4 rounded-lg hover:bg-gray-700 transition-colors text-center flex flex-col items-center space-y-2"
            >
              <Download className="w-6 h-6" />
              <span className="font-medium">Download</span>
              <span className="text-xs opacity-90">Save to device</span>
            </button>
          </div>
        </div>
      );
    }
    
    // For other file types, show preview message and download option
    return (
      <div className="flex flex-col items-center justify-center h-full space-y-4">
        <div className="text-center">
          <div className="text-primary-600 mb-4">
            {getFileIcon(fileType)}
          </div>
          <p className="text-lg font-medium text-gray-800 mb-2">{name}</p>
          <p className="text-gray-600 mb-4">
            This file type cannot be previewed in the browser.
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => handleView(doc)}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
          >
            Open in New Tab
          </button>
          <button
            onClick={() => handleDownload(doc)}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            Download File
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Project Documents</h3>
        <button
          onClick={() => setShowUpload(true)}
          className="btn-primary flex items-center"
        >
          <Plus className="w-4 h-4 mr-2" />
          Upload Document
        </button>
      </div>

      {/* Documents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {project.documents?.map((doc, index) => (
          <motion.div
            key={doc._id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className="text-primary-600">
                  {getFileIcon(doc.fileType)}
                </div>
                <div className="flex-1 min-w-0 pr-2 max-w-48">
                  <p className="font-medium text-gray-800 break-all text-xs leading-tight overflow-hidden" 
                     style={{
                       display: '-webkit-box',
                       WebkitLineClamp: 2,
                       WebkitBoxOrient: 'vertical',
                       maxHeight: '2.4em'
                     }}
                     title={doc.name}>
                    {doc.name}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {doc.fileSize && getFileSize(doc.fileSize)}
                  </p>
                </div>
              </div>
              {userRole === 'admin' && (
                <button
                  onClick={() => handleDelete(doc._id)}
                  className="text-red-500 hover:text-red-700 p-1"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="space-y-2">
              <p className="text-xs text-gray-500">
                Uploaded: {format(new Date(doc.uploadedAt), 'MMM dd, yyyy')}
              </p>
              
              <div className="flex space-x-2">
                <button
                  onClick={() => viewDocument(doc)}
                  className="flex-1 flex items-center justify-center bg-primary-600 text-white px-3 py-2 rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  View
                </button>
                <button
                  onClick={() => handleDownload(doc)}
                  className="flex-1 flex items-center justify-center bg-gray-600 text-white px-3 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </button>
              </div>
            </div>
          </motion.div>
        ))}

        {(!project.documents || project.documents.length === 0) && (
          <div className="col-span-full text-center py-8 text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No documents uploaded yet</p>
            <p className="text-sm">Upload your first document to get started</p>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUpload && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 w-full max-w-md"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">Upload Document</h3>
              <button
                onClick={() => {
                  setShowUpload(false);
                  setSelectedFile(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Drag & Drop Area */}
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-300 hover:border-primary-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {selectedFile ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-center">
                    {getFileIcon(selectedFile.type)}
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">{selectedFile.name}</p>
                    <p className="text-sm text-gray-500">{getFileSize(selectedFile.size)}</p>
                  </div>
                  <button
                    onClick={() => setSelectedFile(null)}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    Remove file
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <Upload className="w-12 h-12 mx-auto text-gray-400" />
                  <div>
                    <p className="text-gray-600">Drop your file here or</p>
                    <label className="text-primary-600 hover:text-primary-700 cursor-pointer font-medium">
                      browse files
                      <input
                        type="file"
                        className="hidden"
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            handleFileSelect(e.target.files[0]);
                          }
                        }}
                      />
                    </label>
                  </div>
                  <p className="text-xs text-gray-500">
                    Supports: PDF, Word, Excel, PowerPoint, Images, Text files, ZIP, RAR
                    <br />
                    Max size: 50MB
                  </p>
                </div>
              )}
            </div>

            {/* Upload Button */}
            {selectedFile && (
              <div className="mt-6 flex space-x-3">
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="flex-1 btn-primary disabled:opacity-50"
                >
                  {uploading ? 'Uploading...' : 'Upload Document'}
                </button>
                <button
                  onClick={() => {
                    setShowUpload(false);
                    setSelectedFile(null);
                  }}
                  className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            )}
          </motion.div>
        </div>
      )}

      {/* Document Viewer Modal */}
      {viewingDocument && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl w-full h-full max-w-6xl max-h-[90vh] flex flex-col"
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="text-primary-600">
                  {getFileIcon(viewingDocument.fileType)}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 truncate max-w-md" title={viewingDocument.name}>
                    {viewingDocument.name}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {viewingDocument.fileSize && getFileSize(viewingDocument.fileSize)}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleDownload(viewingDocument)}
                  className="bg-gray-600 text-white px-3 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </button>
                <button
                  onClick={() => setViewingDocument(null)}
                  className="text-gray-400 hover:text-gray-600 p-2"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-hidden">
              {getViewerContent(viewingDocument)}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default DocumentManager;